import type { AppSettings, ColorSchemeSetting, ThemeStyle } from '@/core/types/settings'

/**
 * Everything about how the app looks, applied as attributes and custom
 * properties on `<html>`.
 *
 * Nothing here reaches into components. A theme switch is one attribute change
 * and the CSS does the rest — which is the entire reason no component is
 * allowed to hard-code a colour (CLAUDE.md rule 6).
 */

export interface ColorThemePreset {
  id: string
  name: string
  /** Accent in light and dark. Both are needed: one value cannot serve both. */
  light: string
  dark: string
}

/**
 * Accent presets.
 *
 * Only the ACCENT changes, not the surfaces. Surfaces are near-black and
 * near-white on purpose — the blocks carry the colour in this app, and a tinted
 * background competes with them.
 */
export const COLOR_THEMES: readonly ColorThemePreset[] = [
  { id: 'default', name: 'Blue', light: '#4a90d9', dark: '#5aa0e9' },
  { id: 'graphite', name: 'Graphite', light: '#5c6a7e', dark: '#8a97aa' },
  { id: 'forest', name: 'Forest', light: '#2f9963', dark: '#4cc188' },
  { id: 'amber', name: 'Amber', light: '#b06b2c', dark: '#dba05f' },
  { id: 'plum', name: 'Plum', light: '#96468e', dark: '#c07ab8' },
  { id: 'crimson', name: 'Crimson', light: '#c03b37', dark: '#e5706c' },
]

export function colorThemeById(id: string): ColorThemePreset {
  return COLOR_THEMES.find((t) => t.id === id) ?? (COLOR_THEMES[0] as ColorThemePreset)
}

const THEME_COLORS: Record<'light' | 'dark', string> = {
  light: '#ffffff',
  dark: '#000000',
}

export function resolveScheme(scheme: ColorSchemeSetting): 'light' | 'dark' {
  if (scheme !== 'system') return scheme
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

export function applyTheme(style: ThemeStyle, scheme: ColorSchemeSetting): void {
  const root = document.documentElement
  root.setAttribute('data-theme', style)

  if (scheme === 'system') root.removeAttribute('data-scheme')
  else root.setAttribute('data-scheme', scheme)

  updateThemeColor(resolveScheme(scheme))
}

/**
 * Applies every appearance setting at once.
 *
 * One function rather than one per setting, because they interact: the accent
 * preset depends on the resolved colour scheme, and the corner radius has to
 * override whatever the theme set.
 */
export function applyAppearance(settings: AppSettings): void {
  const root = document.documentElement
  applyTheme(settings.themeStyle, settings.colorScheme)

  const resolved = resolveScheme(settings.colorScheme)
  const preset = colorThemeById(settings.colorThemeId)
  root.style.setProperty('--accent', resolved === 'dark' ? preset.dark : preset.light)

  // Selection tint is the accent at low alpha. Derived rather than stored, so
  // a preset only ever has to name one colour.
  root.style.setProperty(
    '--selection',
    hexToRgba(resolved === 'dark' ? preset.dark : preset.light, resolved === 'dark' ? 0.35 : 0.25),
  )

  root.toggleAttribute('data-colorblind', settings.colorblindMode)

  // Clamped: the setting is a number and a corrupt or hand-edited blob should
  // not be able to make every block a circle.
  if (settings.blockCornerRadius === null) {
    root.style.removeProperty('--block-radius')
  } else {
    root.style.setProperty(
      '--block-radius',
      `${Math.max(0, Math.min(12, settings.blockCornerRadius))}px`,
    )
  }

  root.style.setProperty('--block-bevel', settings.block3DEffect ? '' : '0px')
  if (settings.block3DEffect) root.style.removeProperty('--block-bevel')

  // 0.5x-2x. A larger number means a SLOWER animation, so it multiplies the
  // duration directly.
  const speed = Math.max(0.5, Math.min(2, settings.animationSpeed))
  root.style.setProperty('--anim-speed', String(speed))

  root.setAttribute('data-mark-anim', settings.markAnimation)
  root.toggleAttribute('data-gridlines', settings.showHourGridlines)
}

function updateThemeColor(resolved: 'light' | 'dark'): void {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
  if (meta) meta.content = THEME_COLORS[resolved]
}

function hexToRgba(hex: string, alpha: number): string {
  const s = hex.replace('#', '')
  const r = parseInt(s.slice(0, 2), 16)
  const g = parseInt(s.slice(2, 4), 16)
  const b = parseInt(s.slice(4, 6), 16)
  return `rgb(${r} ${g} ${b} / ${Math.round(alpha * 100)}%)`
}

/** Returns an unsubscribe function. */
export function watchSystemScheme(onChange: (scheme: 'light' | 'dark') => void): () => void {
  const mq = window.matchMedia('(prefers-color-scheme: dark)')
  const handler = (e: MediaQueryListEvent) => onChange(e.matches ? 'dark' : 'light')
  mq.addEventListener('change', handler)
  return () => mq.removeEventListener('change', handler)
}
